import React, { useEffect, useState, useMemo } from "react";
import { signatureController } from "../../../utils/HomeApis";
import { stateMappingForSSUTA, stateTaxIdMappingForMTC } from "./mapping";

const DataGenerator = ({
  selectedId,
  dataToReturn,
  formData,
  CORAAPIResponse,
}) => {
  const [signID, setSignID] = useState(null);

  useEffect(() => {
    const fetchSignatureId = async () => {
      try {
        const { signature_id } = await signatureController(CORAAPIResponse);
        setSignID(signature_id);
      } catch (error) {
        console.error("Error fetching signature ID:", error);
      }
    };
    fetchSignatureId();
  }, [CORAAPIResponse]);

  const safeValue = (value) =>
    value === undefined || value === null ? "" : value;

  const extractAddressParts = (address) => {
    const parts = address.split(",").map((part) => part.trim());
    return {
      street: parts[0],
      city: parts[1],
      state: parts[2],
      country: parts[3],
      zip: parts[4],
    };
  };

  const generateDataForFormSSUTA = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const { street, city, state, country, zip } = extractAddressParts(
      CORAAPIResponse.address
    );
    const stateData =
      stateMappingForSSUTA[
        CORAAPIResponse.shipping_address?.[0]?.ship_to_state
      ] || {};
    const styleOfBusiness = safeValue(
      CORAAPIResponse.style_of_business.toLowerCase()
    );
    const staticCheckBox = [
      "restaurant",
      "caterer",
      "hospital w/cafeteria",
      "hotel w/restaurant",
      "retailer",
    ];
    const isRetailer = staticCheckBox.includes(styleOfBusiness);
    const isWholesaler = styleOfBusiness === "wholesaler";

    const businessTypes = {
      retailer_10_ssuta: isRetailer,
      wholesaler_13_ssuta: isWholesaler,
      other_20_ssuta: !(isRetailer || isWholesaler),
    };

    const resale_g_susta = isRetailer || isWholesaler;
    const g_other = businessTypes.other_20_ssuta ? "L" : "G";

    return {
      sign_stamp_id: signID,
      "Item.Multistate": safeValue(""),
      "Item.NR.Invoice/PurchaseOrderNumber": safeValue(""),
      "Item.NR.OtherExplain": businessTypes.other_20_ssuta
        ? safeValue(formData.reason)
        : "",
      "Item.NR.FederalGovernment": safeValue(""),
      "Item.NR.StateLocalGovernment": safeValue(""),
      "Item.NR.TribalGovernment": safeValue(""),
      "Item.NR.ForeignDiplomat": safeValue(""),
      "Item.NR.OtherExplination": businessTypes.other_20_ssuta
        ? safeValue(formData.reason)
        : "",
      "Item.Your.Name": safeValue(formData.legalName),
      "Item.BusinessAddress": safeValue(formData.streetAdress),
      "Item.City": safeValue(formData.city),
      "Item.PurchaserStateCountry": safeValue(formData.state),
      "Item.Country": safeValue(CORAAPIResponse.seller_country),
      "Item.BusinessPostalcode": safeValue(formData.zip),
      "Item.NameofSeller": safeValue(CORAAPIResponse.legal_enitity_name),
      "Item.SellersAddress": safeValue(street),
      "Item.SellersCity": safeValue(city),
      "Item.SellersStateCountry": safeValue(state),
      "Item.SellerCountry": safeValue(country),
      "Item.SellersPostalCode": safeValue(zip),
      "Item.Checkbox": safeValue("Yes"),
      "Item.Checkbox2": safeValue("Yes"),
      ...(stateData && {
        [Object.keys(stateData)[0]]: safeValue(formData.stateTaxId),
        [Object.keys(stateData)[1]]: safeValue(
          CORAAPIResponse.shipping_address?.[0]?.ship_to_state
        ),
        [Object.keys(stateData)[2]]: safeValue(g_other),
      }),
      "Item.PrintName": safeValue(formData.name),
      "Item.Title": safeValue(formData.title),
      "Item.Date": safeValue(formData.date),
      Signature: safeValue(formData.name),
      retailer_10_ssuta: safeValue(businessTypes.retailer_10_ssuta),
      wholesaler_13_ssuta: safeValue(businessTypes.wholesaler_13_ssuta),
      other_20_ssuta: safeValue(businessTypes.other_20_ssuta),
      resale_g_susta: safeValue(resale_g_susta),
      other_m_susta: safeValue(businessTypes.other_20_ssuta),
    };
  }, [signID, formData, CORAAPIResponse]);

  const generateDataForFormTexas = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const { street, city, state, zip } = extractAddressParts(
      CORAAPIResponse.address
    );

    return {
      sign_stamp_id_CU4W: safeValue(signID),
      Purchaser: safeValue(formData?.legalName),
      "Purchaser phone": safeValue(formData?.phonenumber),
      "Purchaser address": safeValue(formData?.streetAdress),
      "Purchaser city, state, ZIP": safeValue(
        formData?.city && formData?.state && formData?.zip
          ? `${formData.city}, ${formData.state}, ${formData.zip}`
          : ""
      ),
      "Permit number": safeValue(formData?.stateTaxId),
      RFCnumber: safeValue(formData?.RFCnumber),
      Seller: safeValue(CORAAPIResponse?.legal_enitity_name),
      "Seller street": safeValue(street),
      "Seller city, state, ZIP": `${safeValue(city)}, ${safeValue(
        state
      )}, ${safeValue(zip)}`,
      "Business activity": safeValue(formData?.description),
      "Purchaser title": safeValue(formData?.title),
      "Purchaser sig date": safeValue(formData.date),
      "Exemption purchaser": safeValue(formData?.legalName),
      "Exemption purchaser street": safeValue(formData?.streetAdress),
      "Exemption purchaser phone": safeValue(formData?.phonenumber),
      "Exemption purchaser city": safeValue(
        formData?.city && formData?.state && formData?.zip
          ? `${formData.city}, ${formData.state}, ${formData.zip}`
          : ""
      ),
      "Exempt seller": safeValue(CORAAPIResponse?.legal_enitity_name),
      "Exempt seller street": safeValue(street),
      "Exempt seller city, state, ZIP": `${safeValue(city)}, ${safeValue(
        state
      )}, ${safeValue(zip)}`,
      "Exempt item description":
        "Restaurant related manufacturing equipment, tools, chemicals, and cleaning supplies.",
      "Exemption reason": "34 TAC SEC 3.3000 & SEC 151.318 - MANUFACTURING",
      "Exempt purchaser title": safeValue(formData?.title),
      "Exempt purchaser sig date": safeValue(formData.date),
      "Items description": safeValue(formData.discriptionofitems),
      "Signature Purchaser": safeValue(formData?.name),
      "Signature Purchaser2": safeValue(formData?.name),
    };
  }, [signID, formData, CORAAPIResponse]);

  const generateDataForFormSt12 = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const { street, city, state, zip } = extractAddressParts(
      CORAAPIResponse.address
    );

    return {
      sign_stamp_id: safeValue(signID),
      "Seller’s name": safeValue(CORAAPIResponse.legal_enitity_name),
      "Seller Address": safeValue(street),
      "Sellers City": safeValue(city),
      "Sellers State": safeValue(state),
      "Sellers Zip": safeValue(zip),
      "Description of property": safeValue(formData.discriptionofitems),
      Signature: safeValue(formData.name),
      Title: safeValue(formData.title),
      "Name of Company": safeValue(formData.legalName),
      "Federal Id number": safeValue(formData.stateTaxId),
      Address: safeValue(formData.streetAdress),
      State: safeValue(formData.state),
      Zip: safeValue(formData.zip),
      City: safeValue(formData.city),
      Date: safeValue(formData.date),
      "Blankate Certificate": true,
    };
  }, [signID, formData, CORAAPIResponse]);

  const generateDataForFormMultipleStates = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const styleOfBusiness = CORAAPIResponse.style_of_business.toLowerCase();
    const staticCheckBox = [
      "restaurant",
      "caterer",
      "hospital w/cafeteria",
      "hotel w/restaurant",
      "retailer",
    ];
    const businessTypes = {
      Retailer: staticCheckBox.includes(styleOfBusiness),
      Wholesaler: styleOfBusiness === "wholesaler",
      mtc_other: !(
        staticCheckBox.includes(styleOfBusiness) ||
        styleOfBusiness === "wholesaler"
      ),
    };
    const isOtherSpecifyChecked = businessTypes.mtc_other
      ? safeValue(formData.reason)
      : "";
    const shipToState = safeValue(
      CORAAPIResponse.shipping_address?.[0]?.ship_to_state
    );
    const stateTaxId = stateTaxIdMappingForMTC[shipToState] || "";
    const combinedValue =
      shipToState === "CO" && formData.localTaxId
        ? `${safeValue(formData.stateTaxId)},${safeValue(
            formData.localTaxId
          )}`.trim()
        : safeValue(formData.stateTaxId);

    const description = safeValue(formData.discriptionofitems);
    const seller1Description =
      description.length > 45 ? description.slice(0, 45) : description;
    const seller2Description =
      description.length > 45 ? description.slice(45) : "";
    const updatedMTCFormAddress = safeValue(CORAAPIResponse.address).replace(
      ", US",
      ""
    );
    const descriptionOfBusiness =
      safeValue(formData.description) === "Other"
        ? ""
        : safeValue(formData.description);

    return {
      sign_stamp_id: safeValue(signID),
      "Issued to Seller": safeValue(CORAAPIResponse.legal_enitity_name),
      Address: updatedMTCFormAddress,
      "Name of Firm Buyer": safeValue(formData.legalName),
      Address_2: `${safeValue(formData.streetAdress)}, ${safeValue(
        formData.city
      )}, ${safeValue(formData.state)}, ${safeValue(formData.zip)}`,
      "Description of Business": descriptionOfBusiness,
      "General description of tangible property or taxable services to be purchased from the Seller 1":
        safeValue(seller1Description),
      "General description of tangible property or taxable services to be purchased from the Seller 2":
        safeValue(seller2Description),
      [stateTaxId]: safeValue(combinedValue),
      Signature: safeValue(formData.name),
      Title: safeValue(formData.title),
      Date: safeValue(formData.date),
      ...businessTypes,
      mtc_other_reason: isOtherSpecifyChecked,
    };
  }, [signID, formData, CORAAPIResponse]);

  const generateDataForFormSt10 = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const { street, city, state, zip } = extractAddressParts(
      CORAAPIResponse.address
    );

    return {
      sign_stamp_id: safeValue(signID),
      "Name of supplier": safeValue(CORAAPIResponse.legal_enitity_name),
      "Month, Day and Year": safeValue(formData.date),
      "Number and street or rural route": safeValue(street),
      "Check Box1": true,
      "Name of Dealer": safeValue(formData.legalName),
      "Virginia Account Number": safeValue(formData.stateTaxId),
      "Business Type": safeValue(formData.description),
      Title: safeValue(formData.title),
      "Trading As Address": safeValue(formData.streetAdress),
      City: safeValue(city),
      State: safeValue(state),
      zipcode: safeValue(zip),
      "City of dealer": safeValue(formData.city),
      "State of dealer": safeValue(formData.state),
      "zip code of dealer": safeValue(formData.zip),
      Signature: safeValue(formData.name),
    };
  }, [signID, formData, CORAAPIResponse]);

  const generateDataForFormSt120 = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const { street, city, state, zip } = extractAddressParts(
      CORAAPIResponse.address
    );
    const styleOfBusiness = safeValue(
      CORAAPIResponse.style_of_business.toLowerCase()
    );
    const description = safeValue(formData.discriptionofitems).slice(0, 45);
    const staticCheckBox = [
      "restaurant",
      "caterer",
      "hospital w/cafeteria",
      "hotel w/restaurant",
      "retailer",
      "wholesaler",
      "other",
    ];

    return {
      sign_stamp_id: safeValue(signID),
      "single-use certificate": "Yes",
      "i am engaged in the business of": safeValue(formData.description),
      "and principally sell": description,
      "coa number 1": safeValue(formData.stateTaxId),
      "coa number 2": "",
      "and expires on": "",
      "state/jurisdiction": "",
      "registration number": "",
      "I certify that I am": "",
      "purchaser's name": safeValue(formData.legalName),
      "purchaser's address": safeValue(formData.streetAdress),
      "purchaser city": safeValue(formData.city),
      "purchaser state": safeValue(formData.state),
      "purchaser zip code": safeValue(formData.zip),
      "sellers name": safeValue(CORAAPIResponse.legal_enitity_name),
      "seller's address": safeValue(street),
      "seller city": safeValue(city),
      "seller state": safeValue(state),
      "seller zip code": safeValue(zip),
      "print signer's name": `${safeValue(formData.name)}, ${safeValue(
        formData.title
      )}`,
      "date signed": safeValue(formData.date),
      "Blankate Certificate": true,
      New_york_chekced: staticCheckBox.includes(styleOfBusiness),
      tangable_property_st120_checked: staticCheckBox.includes(styleOfBusiness),
      type_food_st120_checked: [
        "restaurant",
        "caterer",
        "hospital w/cafeteria",
        "hotel w/restaurant",
      ].includes(styleOfBusiness),
      st120_tangible_property_NY: false,
      st120_tangible_outsideNY: false,
      Signature: safeValue(formData.name),
    };
  }, [signID, formData, CORAAPIResponse]);

  const generateDataForFormSt4 = useMemo(() => {
    if (!CORAAPIResponse || !formData) return null;

    const { street, city, state, zip } = extractAddressParts(
      CORAAPIResponse.address
    );

    return {
      sign_stamp_id: safeValue(signID),
      purchaser_name: safeValue(formData.legalName),
      purchaser_address: safeValue(formData.streetAdress),
      purchaser_state: safeValue(formData.state),
      purchaser_zip: safeValue(formData.zip),
      purchaser_city: safeValue(formData.city),
      purchaser_typeof_business: safeValue(formData.description),
      tangible_personal_property: safeValue(formData.discriptionofitems),
      tangible_vendor_name: safeValue(CORAAPIResponse.legal_enitity_name),
      vendor_address: safeValue(street),
      vendor_city: safeValue(city),
      vendor_state: safeValue(state),
      vendor_zip: safeValue(zip),
      purchaser_sign: safeValue(formData.name),
      purchaser_title: safeValue(formData.title),
      current_date: safeValue(formData.date),
      single_purchaser_certificate: false,
      blanket_certificate: false,
      ID_number: safeValue(formData.stateTaxId),
    };
  }, [signID, formData, CORAAPIResponse]);

  useEffect(() => {
    if (signID) {
      let dataGenerated;

      switch (selectedId?.toLowerCase()) {
        case "ssuta":
          dataGenerated = generateDataForFormSSUTA;
          break;
        case "texas_f":
        case "texas_fb":
          dataGenerated = generateDataForFormTexas;
          break;
        case "st_12ec":
          dataGenerated = generateDataForFormSt12;
          break;
        case "mtc":
          dataGenerated = generateDataForFormMultipleStates;
          break;
        case "st_10":
          dataGenerated = generateDataForFormSt10;
          break;
        case "st_120":
          dataGenerated = generateDataForFormSt120;
          break;
        case "st_4":
          dataGenerated = generateDataForFormSt4;
          break;
        default:
          dataGenerated = "Unknown ID"; // Handle any unmatched IDs
          break;
      }

      // Send the processed dataGenerated back to Certificate
      dataToReturn(dataGenerated);
    }
  }, [
    signID,
    selectedId,
    formData,
    CORAAPIResponse,
    generateDataForFormSSUTA,
    generateDataForFormTexas,
    generateDataForFormSt12,
    generateDataForFormMultipleStates,
    generateDataForFormSt10,
    generateDataForFormSt120,
    generateDataForFormSt4,
  ]);

  return null;
};

export default DataGenerator;
